import { useCallback } from 'react';

import { Cart } from '@givebacks/api/store';

import { useCartAction } from '../notifications/actions';
import type { CartId, CartsStorage } from '../types/storage.types';
import { useCartStorage } from './useCartStorage';

type UseShopStorageReturn = {
  /** Adds a new cart to storage */
  addCartToShop: ({
    cartId,
    cart,
    storeId,
  }: {
    cartId: CartId;
    cart: Cart;
    storeId: string;
  }) => void;
  /** Updates a given cart via cartId */
  updateShopCart: ({
    cartId,
    cart,
    storeId,
  }: {
    cartId: CartId;
    cart?: Cart;
    storeId: string;
  }) => void;
  /** Updates or Adds a cart to the shop depending on if there is already a cart with the same storeId */
  updateOrAddCart: ({
    cartId,
    cart,
    storeId,
    emit,
  }: {
    cartId: CartId;
    cart: Cart;
    storeId: string;
    /** Should the update be emitted, false by default */
    emit?: boolean;
  }) => void;

  /** Returns the cartId for a given storeId */
  getCartIdByStoreId: (storeId: string) => string | undefined;
  /** Get Cart by CartId */
  getCartByCartId: (cartId: string) => CartsStorage[number] | undefined;
  /** Removes the specified cart from storage */
  removeCart: (cartId: string) => void;
};

const useShopStorage = (): UseShopStorageReturn => {
  const sendUpdate = useCartAction();
  const { addCart, updateCart, carts, removeCart } = useCartStorage();

  const _addCartToShop: UseShopStorageReturn['addCartToShop'] = useCallback(
    ({ cart, cartId, storeId }) => {
      addCart({
        cart: {
          cartId,
          storeId: storeId,
          itemsCount: cart.cart_items_quantity,
        },
      });
    },
    [addCart]
  );

  const _updateShopCart: UseShopStorageReturn['updateShopCart'] = useCallback(
    ({ cartId, cart, storeId }) => {
      if (!cart) {
        removeCart(cartId);
        return;
      }
      const cartItemCount = cart.cart_items_quantity;
      if (cartItemCount === 0) {
        removeCart(cartId);
      } else {
        updateCart({
          cartId,
          cart: {
            cartId,
            storeId,
            itemsCount: cart.cart_items_quantity,
          },
        });
      }
    },
    [removeCart, updateCart]
  );

  const updateOrAddCart: UseShopStorageReturn['updateOrAddCart'] = useCallback(
    ({ cartId, cart, storeId, emit = false }) => {
      const hasExistingCart = carts.some((cart) => cart.cartId === cartId);
      if (hasExistingCart) {
        _updateShopCart({ cartId, cart, storeId });
      } else {
        _addCartToShop({ cartId, cart, storeId });
      }

      if (emit) {
        sendUpdate({ message: 'Cart updated' });
      }
    },
    [carts, _updateShopCart, _addCartToShop, sendUpdate]
  );

  const getCartIdByStoreId = useCallback(
    (storeId: string) => {
      const cart = carts.find((cart) => cart.storeId === storeId);
      return cart?.cartId;
    },
    [carts]
  );

  const getCartByCartId = useCallback(
    (cartId: string) => {
      return carts.find((cart) => cart.cartId === cartId);
    },
    [carts]
  );

  return {
    addCartToShop: _addCartToShop,
    updateShopCart: _updateShopCart,
    getCartIdByStoreId,
    updateOrAddCart,
    getCartByCartId,
    removeCart,
  };
};

export { useShopStorage };
